// src/lib/copy-to-chambres.js — « 📋 Copier vers les autres chambres »
//
// WAGON R5 LOT C : copie parent l8 → chambres. ADR-197 LOT 3 : la source est
// désormais une CHAMBRE (l8 est non éditable depuis le LOT 2) → on copie d'une
// chambre vers ses SŒURS (lc1 → lc2/lc3). Aucune mutation directe : helpers purs
// consommés par LivretTab/LogementWebsiteTab (modale de confirmation + diff) et
// par LogementsPanel (patch du draft cfg). La persistance passe par le bouton
// 💾 Enregistrer EXISTANT du panel — pas de fire-and-forget ici.
//
// ── Whitelist STRICTE (contenu uniquement) ───────────────────────────────────
// SEULS ces chemins peuvent être copiés. Tout le reste est INTERDIT.
// INTERDITS À JAMAIS (jamais inclus, même en option — décision Dimitri) :
//   - tarifMenage      → vide voulu sur la sous-loc (Dimitri fait le ménage)
//   - caution_amount   → champ argent
//   - stripe_account_id→ paiement
//   - pricing.*        → tarification
//   - tout champ money / facturation / commission (taux, paliers, investissement)
// Ces champs ne figurent NULLE PART dans les définitions de section ci-dessous.
//
import { resolveCheckinCheckoutRaw } from './horaires'

// Convention « vide » : null / undefined / '' sont considérés comme NON renseignés.
// Un champ parent VIDE n'écrase JAMAIS une valeur chambre (on ne propage pas le
// vide). Un objet imbriqué (schedule, capacity) est comparé/copié sous-clé par
// sous-clé (mêmes règles vide), jamais en bloc.

// Type des champs : 'scalar' = champ direct sur le logement ; 'nested' = sous-clé
// d'un objet (ex. schedule.checkin_start) ; 'array' = liste de strings (équipements).
//
// Chaque section regroupe les champs cohérents pour un bouton « Copier vers les
// chambres » dans l'UID concernée.

export const COPY_SECTIONS = [
  {
    id: 'caracteristiques',
    label: '🏠 Caractéristiques (horaires + capacité)',
    fields: [
      { type: 'nested', parent: 'capacity', key: 'max_persons', label: 'Capacité max' },
      // CORRECTIF « heure d'arrivée » (2026-08-05) — type 'horaire' : la valeur
      // qui fait foi pour le voyageur est le champ PLAT `checkin_time` /
      // `checkout_time` (field-first, ADR-199), `schedule.*` n'étant qu'un
      // fallback legacy. Copier `schedule` SEUL n'aurait AUCUN effet sur ce que
      // reçoit le voyageur (le plat de la sœur, non copié, resterait prioritaire)
      // → on copie les DEUX, exactement comme l'éditeur d'horaires les écrit.
      { type: 'horaire', key: 'checkin_time', parent: 'schedule', scheduleKey: 'checkin_start', label: 'Heure début check-in' },
      { type: 'nested', parent: 'schedule', key: 'checkin_end', label: 'Heure fin check-in' },
      { type: 'horaire', key: 'checkout_time', parent: 'schedule', scheduleKey: 'checkout_time', label: 'Heure check-out' },
    ],
  },
  {
    id: 'adresse',
    label: '📍 Adresse / accès',
    fields: [
      { type: 'scalar', key: 'adresse_complete', label: 'Adresse complète' },
      { type: 'scalar', key: 'adresse_precision', label: 'Précision adresse' },
      { type: 'scalar', key: 'instructions_acces_batiment', label: 'Accès bâtiment' },
      { type: 'scalar', key: 'instructions_acces_appartement', label: 'Accès appartement' },
    ],
  },
  // ADR-197 / Front 2 — section WiFi RETIRÉE du copieur : le WiFi est désormais
  // au niveau logement (livret_config.wifi_networks, source unique), pas un champ
  // cfg de chambre. `wifi_instructions` (texte libre) a été supprimé de la saisie.
  // Rien à copier d'une chambre à l'autre côté WiFi.
  {
    id: 'codes',
    label: "🔑 Codes d'accès",
    fields: [
      { type: 'scalar', key: 'box_code', label: 'Code boîte à clés' },
      { type: 'scalar', key: 'door_code', label: 'Code porte' },
      { type: 'scalar', key: 'interphone_code', label: 'Code interphone' },
      { type: 'scalar', key: 'coffre_code', label: 'Code coffre' },
      { type: 'scalar', key: 'garage_code', label: 'Code garage' },
    ],
  },
  {
    id: 'regles',
    label: '📜 Règles / parking / poubelles / livret',
    fields: [
      { type: 'scalar', key: 'parking_info_long', label: 'Parking' },
      { type: 'scalar', key: 'infos_poubelles_long', label: 'Poubelles' },
      { type: 'scalar', key: 'infos_courses_stockage', label: 'Courses & stockage' },
      { type: 'scalar', key: 'regles_maison', label: 'Règles maison' },
      { type: 'scalar', key: 'livret_url', label: 'Livret externe (URL)' },
    ],
  },
  {
    id: 'equipements',
    label: '✨ Équipements',
    fields: [
      { type: 'array', key: 'equipements', label: 'Équipements cochés' },
    ],
  },
]

// Set plat de TOUS les champs autorisés (clé de plus haut niveau touchée).
// Utilisé comme garde défensive : un patch ne peut toucher que ces clés.
// Un champ 'horaire' touche DEUX clés de haut niveau : le champ plat (`key`)
// et l'objet miroir `schedule` (`parent`) — les deux doivent être autorisés.
export const COPY_ALLOWED_TOP_KEYS = new Set(
  COPY_SECTIONS.flatMap(s => s.fields.flatMap(f => {
    if (f.type === 'nested') return [f.parent]
    if (f.type === 'horaire') return [f.key, f.parent]
    return [f.key]
  })),
)

// Champs explicitement INTERDITS — listés pour le test négatif et la lisibilité.
// Ne JAMAIS ajouter ici un champ à la whitelist : c'est l'inverse (liste noire).
export const COPY_FORBIDDEN_KEYS = [
  'tarifMenage', 'caution_amount', 'stripe_account_id', 'pricing',
  'taux', 'paliers', 'investissement', 'commission',
]

function isEmpty(v) {
  if (v == null) return true
  if (typeof v === 'string') return v.trim() === ''
  if (Array.isArray(v)) return v.length === 0
  // QA m-1 : 0 numérique = vide (un parent à capacité 0 est aberrant — ne
  // jamais écraser une valeur chambre avec 0).
  if (typeof v === 'number') return v === 0
  return false
}

// Lit la valeur d'un champ (scalar / nested / array / horaire) sur un logement.
// 'horaire' : on lit la valeur effective via la cascade field-first (celle que
// le voyageur reçoit), normalisée en HH:MM — sinon « 17h » et « 17:00 »
// passeraient pour deux valeurs différentes et la modale afficherait un faux
// changement à chaque ouverture. Variante RAW (sans défaut) : une source qui
// n'a AUCUNE heure saisie doit rester « vide » et donc ne rien propager —
// sinon copier une chambre sans check-out écraserait celui de sa sœur avec le
// défaut 10:00.
function readField(log, field) {
  if (!log) return undefined
  if (field.type === 'nested') {
    const obj = log[field.parent]
    return obj && typeof obj === 'object' ? obj[field.key] : undefined
  }
  if (field.type === 'horaire') {
    const raw = resolveCheckinCheckoutRaw(log)
    return field.key === 'checkin_time' ? raw.checkin : raw.checkout
  }
  return log[field.key]
}

// Représentation lisible d'une valeur pour la modale (tronquée).
export function displayValue(v) {
  if (isEmpty(v)) return '(vide)'
  if (Array.isArray(v)) return v.length ? v.join(', ') : '(vide)'
  const s = String(v)
  return s.length > 80 ? s.slice(0, 80) + '…' : s
}

// Compare deux valeurs (égalité structurelle suffisante pour scalaires + arrays
// de strings). Les arrays sont comparés ordre-insensible (un set d'équipements).
function valuesEqual(a, b) {
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    const sa = [...a].sort()
    const sb = [...b].sort()
    return sa.every((x, i) => x === sb[i])
  }
  return a === b
}

// Calcule, pour une section donnée, le plan de copie d'une chambre SOURCE vers
// ses chambres SŒURS (ADR-197 LOT 3 — la copie part désormais d'une chambre,
// plus du parent l8 devenu non éditable). Retourne un objet par sœur :
//   { chambreId, chambreNom, alreadyUpToDate: bool, changes: [{ key,label,from,to }] }
// `changes` ne contient QUE les champs qui changeront (source non vide ET valeur
// sœur différente). Si aucun changement → alreadyUpToDate=true.
export function buildCopyPlan(sourceLog, targetLogs, sectionId) {
  const section = COPY_SECTIONS.find(s => s.id === sectionId)
  if (!section || !sourceLog) return { section: null, perChambre: [] }
  const targets = Array.isArray(targetLogs) ? targetLogs : []

  const perChambre = targets.map(ch => {
    const changes = []
    for (const field of section.fields) {
      const sourceVal = readField(sourceLog, field)
      // Source vide → on ne propage jamais le vide (ne pas effacer une valeur sœur).
      if (isEmpty(sourceVal)) continue
      const chambreVal = readField(ch, field)
      if (valuesEqual(sourceVal, chambreVal)) continue
      changes.push({
        type: field.type,
        parent: field.parent,
        key: field.key,
        // 'horaire' uniquement : sous-clé du miroir `schedule` à écrire.
        scheduleKey: field.scheduleKey,
        label: field.label,
        from: chambreVal,
        to: sourceVal,
      })
    }
    return {
      chambreId: ch.id,
      chambreNom: ch.nom || ch.id,
      alreadyUpToDate: changes.length === 0,
      changes,
    }
  })

  return { section, perChambre }
}

// True si au moins une chambre recevra au moins un changement (sert à
// activer/désactiver le bouton de confirmation de la modale).
export function planHasChanges(plan) {
  return Boolean(plan && plan.perChambre.some(c => !c.alreadyUpToDate))
}

// Applique le plan à une liste de chambres SŒURS (objets du draft cfg).
// Fonction PURE : retourne un NOUVEAU tableau de sœurs patchées (même ordre),
// ne mute pas les entrées d'origine. Garde défensive : seuls les champs whitelist
// peuvent être écrits (un changement hors COPY_ALLOWED_TOP_KEYS est ignoré).
export function applyCopyPlan(targetLogs, plan) {
  if (!Array.isArray(targetLogs) || !plan) return targetLogs
  const byId = new Map(plan.perChambre.map(c => [c.chambreId, c]))

  return targetLogs.map(ch => {
    const entry = byId.get(ch.id)
    if (!entry || entry.alreadyUpToDate) return ch
    let next = { ...ch }
    for (const change of entry.changes) {
      const topKey = change.type === 'nested' ? change.parent : change.key
      // Garde défensive anti-fuite (whitelist stricte).
      if (!COPY_ALLOWED_TOP_KEYS.has(topKey)) continue
      if (change.type === 'horaire') {
        // Écrit la MÊME paire que l'éditeur d'horaires : champ plat autoritaire
        // + miroir `schedule.*`, pour que le fallback ne contredise jamais la
        // valeur affichée chez la sœur.
        if (!COPY_ALLOWED_TOP_KEYS.has(change.parent)) continue
        next[change.key] = change.to
        next[change.parent] = { ...(next[change.parent] || {}), [change.scheduleKey]: change.to }
      } else if (change.type === 'nested') {
        next[change.parent] = { ...(next[change.parent] || {}), [change.key]: change.to }
      } else if (change.type === 'array') {
        next[change.key] = Array.isArray(change.to) ? [...change.to] : change.to
      } else {
        next[change.key] = change.to
      }
    }
    return next
  })
}
